import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  afterNextRender,
  computed,
  inject,
  input,
} from '@angular/core';
import { MeshGradient, type MeshGradientColorsConfig } from '@mesh-gradient/core';

const DEFAULT_MESH_COLORS: MeshGradientColorsConfig = [
  '#505ED7',
  '#77CEC7',
  '#505ED7',
  '#77CEC7',
];

@Component({
  selector: 'app-mesh-gradient',
  template: `
    <div class="mesh-gradient-bleed" [style.inset]="bleedInset()">
      <canvas class="mesh-gradient-canvas" aria-hidden="true"></canvas>
      <div
        class="mesh-gradient-grain"
        aria-hidden="true"
        [style.opacity]="grainOpacity()"
        [style.backgroundImage]="grainBackgroundImage()"
      ></div>
    </div>
  `,
  styleUrl: './mesh-gradient.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mesh-gradient-host',
  },
})
export class MeshGradientComponent implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private mesh: MeshGradient | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private initRafId: number | null = null;

  readonly speed = input<number>(0.85);
  readonly seed = input<number>(42);
  readonly frequency = input<number | null>(null);
  readonly blurPx = input<number>(10);
  readonly colors = input<MeshGradientColorsConfig>(DEFAULT_MESH_COLORS);
  readonly grainOpacityPercent = input<number>(42);
  readonly grainFrequency = input<number>(0.65);
  readonly grainOctaves = input<number>(3);
  readonly bleedPercent = input<number>(10);

  readonly bleedInset = computed(() => `-${this.bleedPercent()}%`);
  readonly grainOpacity = computed(() =>
    Math.min(1, Math.max(0, this.grainOpacityPercent() / 100))
  );

  readonly grainBackgroundImage = computed(() => {
    const frequency = this.grainFrequency();
    const octaves = this.grainOctaves();
    const svg = `%3Csvg viewBox='0 0 2482 2482' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='${frequency}' numOctaves='${octaves}' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E`;
    return `url("data:image/svg+xml,${svg}")`;
  });

  readonly canvasFilter = computed(() => `blur(${this.blurPx()}px)`);

  constructor() {
    afterNextRender(() => {
      this.tryInitializeMesh();
      this.resizeObserver = new ResizeObserver(() => {
        this.tryInitializeMesh();
      });
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  private tryInitializeMesh(): void {
    if (this.mesh) {
      return;
    }

    if (this.initRafId !== null) {
      cancelAnimationFrame(this.initRafId);
    }

    this.initRafId = requestAnimationFrame(() => {
      const hostElement = this.host.nativeElement;
      const canvas = hostElement.querySelector('canvas');
      if (!canvas) {
        return;
      }

      const { width, height } = hostElement.getBoundingClientRect();
      if (width <= 1 || height <= 1) {
        return;
      }

      canvas.style.filter = this.canvasFilter();

      const gradient = new MeshGradient();
      gradient.init(canvas, {
        colors: this.colors(),
        animationSpeed: this.speed(),
        seed: this.seed(),
        frequency: this.frequency() ?? { x: 0.00014, y: 0.00029, delta: 0.0001 },
      });
      this.mesh = gradient;
      this.initRafId = null;
    });
  }

  ngOnDestroy(): void {
    if (this.initRafId !== null) {
      cancelAnimationFrame(this.initRafId);
      this.initRafId = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mesh?.destroy();
    this.mesh = null;
  }
}
